//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Typography } from '@mui/material'
import { yupEthAddrRequired } from '@shared/Enums'
import { FieldMetaData, StringTypeEnum, toFieldMetaData } from '@shared/SchemaTypes'
import * as React from 'react'
import { ReactElement } from 'react'
import { SchemaDescription, SchemaFieldDescription } from 'yup'
import getDataValue from './getDataValue'
import ViewEthString from './ViewEthString'
import ViewItem from './ViewItem'
import ViewLogo from './ViewLogo'
import ViewNumber from './ViewNumber'
import ViewString from './ViewString'

import assert from 'assert'
import { ViewBoolean } from './ViewBoolean'

import { logOT } from '@shared/loggingShared'

interface ViewFieldProps {
  fieldSchema: SchemaFieldDescription
  dataObject: Record<string, object> | object
  inTable?: boolean
  toolTip?: string
  poolTokenSymbol?: string
  liquidityTokenSymbol?: string
}

export default function ViewField(props: ViewFieldProps): ReactElement {
  const {
    fieldSchema,
    dataObject,
    toolTip,
    poolTokenSymbol: _poolTokenSymbol,
    liquidityTokenSymbol: _liquidityTokenSymbol,
  } = props
  const poolTokenSymbol = _poolTokenSymbol ?? (dataObject as Record<string, string>).poolTokenSymbol ?? 'Token'

  const liquidityTokenSymbol =
    _liquidityTokenSymbol ?? (dataObject as Record<string, string>)?.liquidityTokenSymbol ?? 'err_USDC'

  const schemaDescription = fieldSchema as SchemaDescription
  if (!schemaDescription) {
    return <div>ViewField: schemaDescription is undefined </div>
  }
  assert(schemaDescription, 'ViewField: schemaDescription is undefined ')
  const fieldMetaData = schemaDescription.meta as FieldMetaData

  const propName: string = fieldMetaData.propName ?? 'undefinedPropName'

  const isEthAddr: boolean = fieldMetaData.stringType === StringTypeEnum.ETH_ADDR

  const dataValue = getDataValue(dataObject as Record<string, object | string>, propName)
  const value: string | undefined = dataValue?.toLocaleString() || fieldMetaData.defaultDisplayValue || 'Undefined'

  const label = schemaDescription.label ?? ''
  const fieldDescription = fieldMetaData.description

  logOT.debug('viewField', label, 'Field schema', fieldSchema, dataValue)

  if (fieldSchema.type === 'boolean') {
    const booleanValue = (dataObject as Record<string, boolean | undefined>)[propName]
    return <ViewBoolean value={booleanValue} inTable={props.inTable ?? false} label={label} />
  }

  if (!value && fieldSchema.type !== 'array') {
    logOT.debug('EMPTY ViewField', propName, dataValue)
    return <div></div>
  }

  if (propName === 'logo' && value && value !== 'undefined') {
    return <ViewLogo size={48} addrOrId={value} />
  }

  const stringType = fieldMetaData.stringType as string | undefined

  if (fieldSchema.type === 'string' && value) {
    if (isEthAddr) {
      return (
        <ViewEthString
          value={value}
          inTable={props.inTable ?? false}
          label={label}
          toolTip={toolTip ?? fieldDescription}
        />
      )
    }

    if (stringType === StringTypeEnum.EXCHANGE_RATE || stringType === StringTypeEnum.EXCHANGE_RATE_LONG) {
      return (
        <ViewNumber
          dataValue={dataValue}
          value={value}
          inTable={props.inTable ?? false}
          label={label}
          fieldSchema={fieldSchema}
          toolTip={toolTip ?? fieldDescription}
          poolTokenSymbol={poolTokenSymbol}
          liquidityTokenSymbol={liquidityTokenSymbol}
        />
      )
    }
    if (
      stringType === StringTypeEnum.STABLE_COINS ||
      stringType === StringTypeEnum.PPT_SHARES ||
      stringType === StringTypeEnum.APR
    ) {
      return (
        <ViewNumber
          dataValue={dataValue}
          value={value}
          inTable={props.inTable ?? false}
          label={label}
          fieldSchema={fieldSchema}
          toolTip={toolTip ?? fieldDescription}
          poolTokenSymbol={poolTokenSymbol}
          liquidityTokenSymbol={liquidityTokenSymbol}
        />
      )
    }

    return (
      <ViewString value={value} inTable={props.inTable ?? false} label={label} toolTip={toolTip ?? fieldDescription} />
    )
  }
  if (fieldSchema.type === 'number') {
    return (
      <ViewNumber
        value={value}
        inTable={props.inTable ?? false}
        label={label}
        fieldSchema={fieldSchema}
        dataValue={dataValue}
        toolTip={toolTip ?? fieldDescription}
        poolTokenSymbol={poolTokenSymbol}
        liquidityTokenSymbol={liquidityTokenSymbol}
      />
    )
  }
  if (fieldSchema.type === 'mixed') {
    if (props.inTable) {
      return (
        <Typography variant="caption" gutterBottom component="div">
          {value && value.length > 0 ? value[0].toUpperCase() + value.substr(1) : ''}
        </Typography>
      )
    }
    return (
      <ViewItem toolTip={toolTip ?? fieldDescription}>
        <Typography variant="bodyMuted" gutterBottom component="div">
          {label}
        </Typography>
        <Typography variant="bodyBold" gutterBottom component="div">
          {value}
        </Typography>
      </ViewItem>
    )
  }
  if (fieldSchema.type === 'date') {
    logOT.debug('Date dataValue', typeof dataValue, new Date(dataValue).toLocaleDateString(), value)
    return (
      <ViewItem toolTip={toolTip}>
        <Typography variant="bodyMuted" gutterBottom component="div">
          {label}
        </Typography>
        <Typography variant="bodyBold" gutterBottom component="div">
          {
            // prettier-ignore
            typeof dataValue === 'string'
            ? new Date(dataValue).toLocaleDateString()
            : dataValue instanceof Date
            ? dataValue.toLocaleDateString()
            : value
          }
        </Typography>
      </ViewItem>
    )
  }
  if (fieldSchema.type === 'array') {
    const v2 = JSON.stringify(dataValue)
    logOT.debug('View array', v2)
    if (props.inTable) {
      return (
        <React.Fragment>
          <Typography variant="caption" gutterBottom component="div">
            {v2}
          </Typography>
        </React.Fragment>
      )
    }
    logOT.debug(fieldSchema)

    return (
      <ViewItem toolTip={toolTip ?? fieldDescription}>
        <Typography variant="bodyMuted" gutterBottom component="div">
          {label}
        </Typography>
        <Box sx={{ flexDirection: 'column' }}>
          {(dataValue as Record<string, string>[]).map((value, index) => {
            const ethSchema = yupEthAddrRequired.meta(
              toFieldMetaData({
                propName: index.toString(),
                stringType: StringTypeEnum.ETH_ADDR,
              })
            )
            return (
              <ViewField
                poolTokenSymbol={poolTokenSymbol}
                liquidityTokenSymbol={liquidityTokenSymbol}
                key={index}
                fieldSchema={ethSchema.describe()}
                dataObject={value}
              />
            )
          })}
        </Box>
      </ViewItem>
    )
  }
  logOT.error('UNDEFINED TYPE FOR EDIT ', fieldSchema.type, fieldSchema)
  return (
    <React.Fragment>
      <Typography variant="bodyMuted" gutterBottom component="div">
        {label}
      </Typography>
      <Typography variant="inherit" color="textSecondary">
        Undefined Type {fieldSchema.type}
      </Typography>
    </React.Fragment>
  )
}
